<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">10 kỹ thuật prompt ChatGPT</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">10 kỹ thuật prompt ChatGPT hiệu quả nhất 2024</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>10/12/2024</span>
          <span class="mx-2">•</span>
          <span>8 phút đọc</span>
          <span class="mx-2">•</span>
          <span>AI & Công nghệ</span>
        </div>
        <div class="h-64 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
          <svg class="w-24 h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Prompt là gì và tại sao quan trọng?</h2>
          <p>
            Prompt (câu lệnh) là cách bạn giao tiếp với ChatGPT. Một prompt tốt sẽ giúp AI hiểu 
            chính xác ý định của bạn và tạo ra kết quả chất lượng cao. Việc viết prompt hiệu quả 
            là kỹ năng quan trọng để tận dụng tối đa sức mạnh của AI.
          </p>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">I. 10 kỹ thuật prompt ChatGPT hiệu quả</h2>

          <div class="space-y-8">
            <div class="border-l-4 border-blue-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">1. 🎭 Chiến lược "Đóng vai Chuyên gia"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Viết lại cái này."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Hãy viết lại nội dung này như một chuyên gia copywriter cấp cao của Apple với 15+ năm kinh nghiệm kể chuyện thuyết phục. Sử dụng ngôn ngữ chuyên ngành, từ ngữ hấp dẫn, và xây dựng mạch truyện thu hút trong khi ngầm thúc đẩy hành động."</p>
              </div>
              <p class="text-gray-700">
                Kỹ thuật này giúp ChatGPT tập trung vào góc độ chuyên môn cụ thể và đưa ra lời khuyên sâu sắc hơn.
              </p>
            </div>

            <div class="border-l-4 border-green-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">2. 🔍 Kỹ thuật "Truy tìm điểm yếu"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Sửa giúp tôi."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Sửa nội dung này bằng cách xác định các điểm yếu về độ rõ ràng, cấu trúc và khả năng thu hút. Làm cho câu từ chính xác hơn, tăng sự mượt mà khi đọc và nâng cao sức ảnh hưởng mà vẫn giữ nguyên giọng văn ban đầu."</p>
              </div>
              <p class="text-gray-700">
                Cung cấp thông tin chi tiết về tình huống giúp AI đưa ra giải pháp phù hợp và thực tế.
              </p>
            </div>

            <div class="border-l-4 border-purple-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">3. ⚡ Phương pháp "Nâng cấp độ chính xác"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Làm cho hay hơn."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Làm cho nội dung này hay hơn bằng cách tăng độ rõ ràng, tăng tính thuyết phục và đánh mạnh vào cảm xúc. Tối ưu cách dùng từ, thêm nhịp điệu cho câu chữ và sắp xếp bố cục sao cho cuốn hút tối đa."</p>
              </div>
              <p class="text-gray-700">
                Đưa ra ví dụ cụ thể giúp AI hiểu rõ phong cách và định dạng bạn mong muốn.
              </p>
            </div>

            <div class="border-l-4 border-yellow-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">4. 🎯 Công thức "Tối ưu theo đối tượng"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Cải thiện nội dung này."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Cải thiện nội dung này cho đúng đối tượng [mục tiêu]. Màu sắc thông điệp, điều chỉnh giọng văn phù hợp và tái cấu trúc để dễ hiểu, thuyết phục mà vẫn chân thực."</p>
              </div>
              <p class="text-gray-700">
                Yêu cầu AI giải thích từng bước giúp bạn hiểu rõ quy trình và logic đằng sau câu trả lời.
              </p>
            </div>

            <div class="border-l-4 border-red-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">5. 📝 Mẹo "Tóm gọn quyền lực"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Tóm tắt giúp."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Tóm tắt nội dung này thành một câu súc tích như dòng tweet — rõ ràng, ấn tượng và khơi gợi tò mò. Thêm một phiên bản khác tối ưu hóa cho khả năng giữ chân người đọc."</p>
              </div>
              <p class="text-gray-700">
                Đặt giới hạn về độ dài, phong cách, hoặc đối tượng giúp AI tạo ra nội dung phù hợp hơn.
              </p>
            </div>

            <div class="border-l-4 border-indigo-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">6. 💡 Cách tiếp cận "Giải thích tinh gọn"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Giải thích cái này."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Giải thích khái niệm này bằng phép ẩn dụ, ví dụ sinh động hoặc câu chuyện dễ hiểu mà vẫn giữ được chiều sâu. Giữ cho nó hấp dẫn, sống động và dễ tiếp cận."</p>
              </div>
              <p class="text-gray-700">
                Không ngại yêu cầu AI chỉnh sửa và cải thiện kết quả dựa trên phản hồi của bạn.
              </p>
            </div>

            <div class="border-l-4 border-pink-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">7. 💭 Mẹo "Máy phát ý tưởng"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Cho tôi vài ý tưởng."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Hãy đưa ra 10 ý tưởng sáng tạo và có tác động cao về [chủ đề]. Mỗi ý tưởng cần độc đáo, khả thi và có điểm nhấn khác biệt khiến nó nổi bật giữa đám đông."</p>
              </div>
              <p class="text-gray-700">
                Chỉ định rõ định dạng đầu ra giúp bạn dễ dàng sử dụng kết quả hơn.
              </p>
            </div>

            <div class="border-l-4 border-teal-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">8. 🎪 Mô hình "Phản hồi thu hút"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Viết câu trả lời giúp."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Viết một câu trả lời sinh động, thuyết phục và đầy cảm xúc. Dùng yếu tố kể chuyện, biện pháp tu từ và giọng văn trò chuyện để không thể cưỡng lại."</p>
              </div>
              <p class="text-gray-700">
                Điều chỉnh mức độ sáng tạo phù hợp với mục đích sử dụng.
              </p>
            </div>

            <div class="border-l-4 border-orange-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">9. 🏢 Biến hóa "Giọng văn chuyên nghiệp"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Viết cho chuyên nghiệp hơn."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Hãy làm cho đoạn này mang phong cách chuyên nghiệp bằng cách chỉnh sửa cấu trúc, nâng cao độ chính xác và tăng tính thuyết phục. Phải thật mượt mà, đáng tin cậy và phù hợp với độc giả cấp cao."</p>
              </div>
              <p class="text-gray-700">
                Chia nhỏ yêu cầu phức tạp thành nhiều bước để có kết quả tốt hơn.
              </p>
            </div>

            <div class="border-l-4 border-cyan-500 pl-4">
              <h3 class="text-xl font-semibold mb-3">10. 📋 Chiến lược "Người hướng dẫn từng bước"</h3>
              <div class="bg-gray-100 p-4 rounded-lg my-4">
                <p class="font-semibold text-gray-800 mb-2">❌ Người mới:</p>
                <p class="text-gray-600 mb-3">"Giúp tôi với cái này."</p>
                <p class="font-semibold text-green-800 mb-2">✅ Chuyên gia:</p>
                <p class="text-green-700">"Hãy giúp tôi bằng cách chia nhỏ ra thành từng bước cụ thể. Giải thích từng bước, chia sẻ mẹo hay và cảnh báo những sai lầm dễ gặp để đảm bảo thực hiện trơn tru."</p>
              </div>
              <p class="text-gray-700">
                Yêu cầu AI tự đánh giá và cải thiện kết quả của chính nó.
              </p>
            </div>
          </div>

          <h2 class="mt-8 mb-2 text-3xl font-bold text-gray-900">II. Mẹo sử dụng prompt hiệu quả</h2>
          
          <div class="bg-blue-50 p-4 rounded-lg my-6">
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Rõ ràng và cụ thể:</strong> Tránh câu hỏi mơ hồ</li>
              <li>• <strong>Thử nghiệm:</strong> Thử nhiều cách diễn đạt khác nhau</li>
              <li>• <strong>Lưu trữ:</strong> Giữ lại những prompt hiệu quả để tái sử dụng</li>
              <li>• <strong>Học hỏi:</strong> Quan sát cách AI phản hồi để cải thiện prompt</li>
              <li>• <strong>Kết hợp:</strong> Sử dụng nhiều kỹ thuật cùng lúc</li>
            </ul>
          </div>

          <h2 class="mt-8 mb-2 text-3xl font-bold text-gray-900">III. Ví dụ prompt hoàn chỉnh</h2>
          
          <div class="bg-green-50 p-6 rounded-lg my-6">
            <h4 class="font-semibold text-green-800 mb-3">💡 Prompt mẫu:</h4>
            <p class="text-green-700 mb-4">
              "Bạn là một chuyên gia SEO với 8 năm kinh nghiệm. Tôi cần bạn giúp tôi tối ưu hóa website bán hàng mỹ phẩm online. Website hiện tại có 50 sản phẩm, chủ yếu phục vụ phụ nữ 25-40 tuổi ở Việt Nam. Hãy tạo một kế hoạch SEO 3 tháng với các bước cụ thể, bao gồm: từ khóa mục tiêu, cấu trúc nội dung, và các hoạt động technical SEO. Trình bày theo định dạng markdown với timeline rõ ràng."
            </p>
          </div>

          <h2 class="text-3xl font-bold text-gray-900">IV. Kết luận</h2>
          <p>
            Việc viết prompt hiệu quả là kỹ năng có thể học và cải thiện theo thời gian. 
            Bằng cách áp dụng 10 kỹ thuật trên, bạn sẽ có thể tận dụng tối đa sức mạnh 
            của ChatGPT và các AI khác để phục vụ công việc và cuộc sống.
          </p>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const title = '10 kỹ thuật prompt ChatGPT hiệu quả nhất 2024'
const desc = 'Khám phá 10 kỹ thuật viết prompt ChatGPT hiệu quả nhất 2024. Hướng dẫn chi tiết cách tận dụng tối đa sức mạnh AI'
const url = 'https://webservices.vn/blog/10-ky-thuat-prompt-chatgpt'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
